/**
 * Formatting utilities for display
 * Multi-currency money formatting (reporting defaults to CHF)
 */

/**
 * Format number as money in the given ISO currency (default CHF for reporting).
 * @param {number} value
 * @param {string} [currency='CHF']
 * @returns {string}
 */
export function formatMoney(value, currency = 'CHF') {
  const code = (currency || 'CHF').toUpperCase();
  if (value === undefined || value === null || isNaN(value)) {
    return `${code} 0`;
  }

  try {
    const formatter = new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(value);
  } catch {
    // Some runtimes reject rare ISO codes — still show a readable amount.
    const num = new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
    return `${code} ${num}`;
  }
}

/**
 * Format number as CHF (reporting currency alias).
 * @param {number} value
 * @returns {string}
 */
export function formatCurrency(value) {
  return formatMoney(value, 'CHF');
}

/**
 * Format number with thousand separators
 * @param {number} value
 * @param {number} decimals
 * @returns {string}
 */
export function formatNumber(value, decimals = 0) {
  if (value === undefined || value === null || isNaN(value)) {
    return '0';
  }

  const formatter = new Intl.NumberFormat('de-CH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return formatter.format(value);
}

/**
 * Format decimal as percentage
 * @param {number} value - Value to format (0.5 = 50%)
 * @returns {string}
 */
export function formatPercent(value) {
  if (value === undefined || value === null || isNaN(value)) {
    return '0%';
  }

  return `${(value * 100).toFixed(0)}%`;
}

/**
 * Format large numbers in compact notation
 * @param {number} value
 * @returns {string}
 */
export function formatCompact(value) {
  if (value === undefined || value === null || isNaN(value)) {
    return '0';
  }

  const formatter = new Intl.NumberFormat('de-CH', {
    notation: 'compact',
    compactDisplay: 'short',
  });

  return formatter.format(value);
}

/**
 * Short FX chip label, e.g. "EUR→CHF 0.932 · 20 Aug"
 */
export function formatFxChip(fx) {
  if (!fx || !fx.rate) return null;
  if (fx.base === fx.quote || fx.source === 'identity') return null;
  const rateStr = Number(fx.rate).toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  let datePart = '';
  if (fx.asOf) {
    try {
      const d = new Date(fx.asOf);
      if (!isNaN(d.getTime())) {
        datePart = ` · ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
      }
    } catch { /* ignore */ }
  }
  const mode = fx.mode === 'frozen' ? ' frozen' : fx.stale ? ' stale' : fx.mode === 'manual' ? ' manual' : '';
  return `${fx.base}→${fx.quote} ${rateStr}${datePart}${mode}`;
}
